import {
    ICorporationResponse,
    ILocationResponse,
    NotificationType,
} from '@kcftech/prometheus-client';
import { DataSourceTable } from 'components/data_source_table/DataSourceTable';
import { TableColumn } from 'components/data_table/types';
import { ArrowLinkCell } from 'components/link/ArrowLink';
import { ActionMenuTableCell } from 'components/menu/ActionMenuTableCell';
import { NotificationSubscriptionHierarchy } from 'components/notification_subscriptions/NotificationSubscriptionHierarchy';
import {
    GroupedNotificationSubscriptionsPagedDataSource,
    NotificationSubscriptionGroup,
} from 'custom_hooks/data_hooks/data_source/groupedNotificationSubscriptionsDataSource';
import { notificationTypeHasGroupId } from 'custom_hooks/data_hooks/data_source/notificationSubscriptionsDataSource';
import { GroupedNotificationSubscriptionsDeleteButton } from 'pages/Settings/NotificationSubscriptions/GroupedNotificationSubscriptionsDeleteButton';
import { NotificationSubscriptionGroupSubscribers } from 'pages/Settings/NotificationSubscriptions/NotificationSubscriptionGroupSubscribers';
import {
    getNotificationSubscriptionRowKey,
    getNotificationTypeDisplayName,
    getSubscriptionGroupId,
    notificationTypeToTabName,
} from 'pages/Settings/NotificationSubscriptions/shared';
import { useMemo } from 'react';
import { useRouteMatch } from 'react-router-dom';
import { excludeNullAndUndefined } from 'utils/array';
import { appendToRoute } from 'utils/RouteHelper';

const getGroupDetailUrl = (nsg: NotificationSubscriptionGroup): string => {
    const groupId = getSubscriptionGroupId(nsg);

    const typePrefix = notificationTypeToTabName[nsg.discriminator];
    const disambiguatorFragment = getUrlIdDisambiguatorPathFragment(nsg, groupId);
    const id = groupId ?? nsg.Location.LocationId;
    return excludeNullAndUndefined([typePrefix, disambiguatorFragment, id]).join('/');
};

function getUrlIdDisambiguatorPathFragment(
    nsg: NotificationSubscriptionGroup,
    groupId: string | undefined,
): string | undefined {
    if (!notificationTypeHasGroupId(nsg.discriminator as NotificationType)) {
        return undefined;
    }
    if (groupId) {
        return 'groupDetail';
    }
    return 'locationDetail';
}

export type GroupedNotificationSubscriptionsTableProps = Readonly<{
    location?: ILocationResponse;
    corporation?: ICorporationResponse;
    dataSource: GroupedNotificationSubscriptionsPagedDataSource;
}>;

export function GroupedNotificationSubscriptionsTable({
    corporation,
    dataSource,
}: GroupedNotificationSubscriptionsTableProps) {
    const { reloadPage } = dataSource;
    const { url } = useRouteMatch();

    const columns = useMemo<TableColumn<NotificationSubscriptionGroup>[]>(
        () => [
            {
                header: () => 'Notification Type',
                id: 'Type',
                value: ({ rowData }) => <>{getNotificationTypeDisplayName(rowData)}</>,
            },
            {
                header: () => 'Path',
                id: 'Path',
                value: ({ rowData }) => (
                    <div className="text-nowrap">
                        <ArrowLinkCell
                            to={appendToRoute(url, getGroupDetailUrl(rowData))}
                        >
                            <NotificationSubscriptionHierarchy
                                subscription={rowData}
                                showLocation
                                showCorporation={corporation === undefined}
                            />
                        </ArrowLinkCell>
                    </div>
                ),
                primary: true,
                locked: true,
                sortOn: ['CorporationName', 'LocationName', 'GroupName'],
            },
            {
                header: () => 'Subscribers',
                id: 'Subscribers',
                value: ({ rowData }) => (
                    <NotificationSubscriptionGroupSubscribers rowData={rowData} />
                ),
            },
            {
                header: () => 'Actions',
                id: 'Actions',
                locked: true,
                value: ({ rowData }) => (
                    <ActionMenuTableCell>
                        <GroupedNotificationSubscriptionsDeleteButton
                            groupedSubscription={rowData}
                            onDelete={reloadPage}
                        />
                    </ActionMenuTableCell>
                ),
            },
        ],
        [corporation, reloadPage, url],
    );

    return (
        <DataSourceTable
            dataSource={dataSource}
            rowKey={getNotificationSubscriptionRowKey}
            columns={columns}
            noResultMessage="No notification subscriptions found."
        />
    );
}
